import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { HttpUtils } from '../utils/http-utils';
import { AdsTxtStatus } from '../domain/adstxt-status.model';
import { Domain } from '../domain/domain.model';
import { DomainWebStats } from '../../kitchen/shared/domain/domain-webstats.model';
import { AdsTxtDiff } from '../components/domain-specific/adstxt-diff-dialog.component';
import { SiteApprovalRequest, SiteApprovalResponse, SiteListResponse } from '../domain/site-approval-request.model';
import { DomainDpApproval } from '../domain/domain-dp-approval.model';
import { DomainDpChangedRequest, DomainDpChangedResponse, DpDomainApprovalsSummary } from '../domain/custom-domain-dp-approval.model';
import { BridgeAdUnitDpHbId } from '../domain/bridge-ad-unit-hb-id.model';
import { SitesCsvImportResult } from '../../demand-partner/domain/sites-csv-import-result.model';
import {AppApprovalRequest, AppApprovalResponse} from '../domain/app-approval-request.model';
import {App} from '../domain/app.model';

@Injectable()
export class AdStatsDomainsService {

  constructor(private http: HttpClient) {
  }

  approvalDpSummary(publisherId: string, type: string): Observable<DpDomainApprovalsSummary> {
    return this.http.get<DpDomainApprovalsSummary>(`${environment.serviceUrl}/ad-stats/domains/approval/dp-summary?type=${type}`, {
      params: HttpUtils.getHttpParams({
        publisherId: publisherId
      })
    });
  }

  approvalDpChanges(req: DomainDpChangedRequest): Observable<DomainDpChangedResponse> {
    return this.http.get<DomainDpChangedResponse>(`${environment.serviceUrl}/ad-stats/domains/approval/dp-changes`, {
      params: HttpUtils.getHttpParams(req)
    });
  }

  approvalDpChangesExport(req: DomainDpChangedRequest) {
    return this.http.get(`${environment.serviceUrl}/ad-stats/domains/approval/dp-changes/export`, {
      responseType: 'text',
      params: HttpUtils.getHttpParams(req)
    });
  }

  approvalDpChangesImport(file: File, type: string) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`${environment.serviceUrl}/ad-stats/domains/approval/dp-changes/import?type=${type}`, formData);
  }

  approvalDpChangesAffectedUnits(dp: string, domainId: string): Observable<BridgeAdUnitDpHbId[]> {
    return this.http.get<BridgeAdUnitDpHbId[]>(`${environment.serviceUrl}/ad-stats/domains/approval/dp-changes/affected-units`, {
      params: {
        dp: dp,
        domainId: domainId
      }
    });
  }

  siteApprovalExport(params: SiteApprovalRequest) {
    return this.http.get(`${environment.serviceUrl}/ad-stats/domains/site-approval/export`, {
      responseType: 'text',
      params: HttpUtils.getHttpParams(params)
    });
  }

  siteApproval(params: SiteApprovalRequest): Observable<SiteApprovalResponse> {
    return this.http.get<SiteApprovalResponse>(`${environment.serviceUrl}/ad-stats/domains/site-approval?type=${params.type}`, {
      params: HttpUtils.getHttpParams(params)
    });
  }

  siteApprovalUpdate(app: DomainDpApproval[]): Observable<DomainDpApproval[]> {
    return this.http.put<DomainDpApproval[]>(`${environment.serviceUrl}/ad-stats/domains/dp-site-approval`, app);
  }

  siteList(params: SiteApprovalRequest): Observable<SiteListResponse> {
    return this.http.get<SiteListResponse>(`${environment.serviceUrl}/ad-stats/domains/site-list`, {
      params: HttpUtils.getHttpParams(params)
    });
  }

  domainsExport(params: SiteApprovalRequest) {
    return this.http.get(`${environment.serviceUrl}/ad-stats/domains/export`, {
      responseType: 'text',
      params: HttpUtils.getHttpParams(params)
    });
  }

  siteApprove(domainIds: string[]): Observable<Domain[]> {
    return this.http.put<Domain[]>(`${environment.serviceUrl}/ad-stats/adu/domain/approve`, domainIds);
  }

  appApprove(domainIds: string[]): Observable<App[]> {
    return this.http.put<App[]>(`${environment.serviceUrl}/ad-stats/adu/domain/approve`, domainIds);
  }

  iasImport(file: File, type: string) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`${environment.serviceUrl}/ad-stats/domains/import-ias?type=${type}`, formData);
  }

  iasExport(domainIds: string[]) {
    return this.http.post(`${environment.serviceUrl}/ad-stats/domains/export-ias`, domainIds, {
      responseType: 'text',
    });
  }

  siteReject(domainIds: string[], reason: string): Observable<Domain[]> {
    return this.http.put<Domain[]>(`${environment.serviceUrl}/ad-stats/adu/domain/reject`, {
      rejectionReason: reason,
      domainIds: domainIds
    });
  }

  siteArchive(domainId: string) {
    return this.http.put(`${environment.serviceUrl}/ad-stats/domains/${domainId}/archive`, {});
  }

  domains(page, size, url_like: string, publisherId: string, statuses?: AdsTxtStatus[], directStatuses?: AdsTxtStatus[]): Observable<any> {
    return this.http.get(`${environment.serviceUrl}/ad-stats/domains`, {
      params: HttpUtils.getHttpParams({
        size: size,
        page: page,
        url_like: url_like,
        status: statuses,
        directStatus: directStatuses,
        pub: publisherId
      })
    });
  }

  webstats(domain: Domain): Observable<DomainWebStats> {
    return this.http.get<DomainWebStats>(`${environment.serviceUrl}/ad-stats/domains/${domain.id}/webstats`);
  }

  checkWebStats(domain: Domain): Observable<DomainWebStats> {
    return this.http.get<DomainWebStats>(`${environment.serviceUrl}/ad-stats/webstats/check-domain?domain=${domain.url}`);
  }

  domainsAdstxtDiff(domain: Domain): Observable<AdsTxtDiff> {
    return this.http.get<AdsTxtDiff>(`${environment.serviceUrl}/ad-stats/domains/${domain.id}/adstxt`);
  }

  domainsCheckAdstxt(url: string | string[]) {
    return this.http.get(`${environment.serviceUrl}/ad-stats/domains/check-adstxt`, {
      params: HttpUtils.getHttpParams({
        url: url
      })
    });
  }

}
